import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { KTIcon, KTSVG } from '../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import {
  change_active_status_product,
  delete_product,
  // get_all_filters_status_products, // This might be for dropdowns, not main list
  // get_all_products, // Will be replaced by get_filtered_products
  get_filtered_products_new
} from '../api/productsApi';
import { mediaPath } from '../../../helper-functions/ImageStoragePath';
import { formatDateWithTime } from '../../../helper-functions/DateFunction';
import { Form } from 'react-bootstrap';
import { get_all_categories } from '../../categories/api/categoriesApi';
import { get_all_subcategories } from '../../subcategories/api/subcategoriesApi';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { getSettingValue } from '../../../helper-functions/general_settings_helper';

const imagePath = `${mediaPath()}/rejected.png`; // Default/fallback image

// Type for a single Product
type Product = {
  id: number;
  name: string | undefined;
  price: number | undefined;
  categories_id: number | undefined;
  category_title: string | undefined;
  sub_categories_id: number | undefined;
  sub_category_title: string | undefined;
  short_description: string | undefined;
  long_description: string | undefined;
  picture: string | undefined;
  remaining_stock_quantity: number | undefined;
  status: number; // 0 or 1
  updated_by_name: string | undefined;
  updated_at: string;
  // Add any other fields your API's 'products' array items have
};

// Category and Subcategory types for filter dropdowns
interface Category {
  id: number;
  title: string;
  // categories_id: number; // Might not be needed if it's a top-level category
  // category_title: string;
};
interface Subcategory {
  id: number;
  title: string;
  categories_id: number; // To link to parent category
  // categories_title: string;
  // sub_categories_id: number; // This is its own ID
  // sub_category_title: string;
};


// --- Types for API Response (Similar to UsersList) ---
type ApiPaginationInfo = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
};

type ApiProductsDataPayload = {
    products: Product[]; // Changed from 'users' to 'products'
    pagination: ApiPaginationInfo;
    links: { /* ... link structure ... */ };
};

type ApiProductsFullResponse = {
    data: { // From Axios
        success: boolean;
        message: string;
        data: ApiProductsDataPayload; // From your API structure
    };
};
// --- End API Response Types ---


var product_add: boolean, product_edit: boolean, product_view: boolean, product_logs: boolean, product_delete: boolean;

function ProductsList() {
  const [productList, setProductList] = useState<Product[]>([]); // For current page's products
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]); // All subcategories fetched
  const [filteredSubcategoriesForDropdown, setFilteredSubcategoriesForDropdown] = useState<Subcategory[]>([]); // For dropdown based on selected category


  // Filter States
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | null>(null); // Renamed from filteredSubcategory
  const [selectedStatus, setSelectedStatus] = useState<string>(''); // Can be '0', '1', or ''
  const [searchInput, setSearchInput] = useState<string>(''); // For client-side search (optional)

  // State for API filter payload
  const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
    categoryId: null,
    subcategoryId: null,
    status: '',
    // searchTerm: '', // If backend supports search
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed from selectedItemsPerPage
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
  const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);

  // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // Server-side sorting needed

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  product_add = permissionCheck('product-create');
  product_edit = permissionCheck('product-update');
  product_view = permissionCheck('product-view');
  product_logs = permissionCheck('product-activity-log');
  product_delete = permissionCheck('product-delete');

  // Fetch categories for filter dropdown
  const { data: categoryData } = useQuery('all_categories_for_filter', get_all_categories, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data && Array.isArray(data.data)) {
        setCategories(data.data as Category[]);
      }
    }
  });

  // Fetch all subcategories once for filter dropdown
  const { data: allSubcategoryData } = useQuery('all_subcategories_for_filter', get_all_subcategories, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data && Array.isArray(data.data)) {
        setSubcategories(data.data as Subcategory[]);
      }
    }
  });

  // Effect to filter subcategories for dropdown when a category is selected
  useEffect(() => {
    if (selectedCategoryId && allSubcategoryData?.data) {
      const filtered = (allSubcategoryData.data as Subcategory[]).filter(
        (sub) => sub.categories_id === selectedCategoryId
      );
      setFilteredSubcategoriesForDropdown(filtered);
    } else {
      setFilteredSubcategoriesForDropdown([]); // Or show all if no category selected
    }
  }, [selectedCategoryId, allSubcategoryData]);


  const itemsPerPageOptions = [10, 20, 30, 50, 100];

  // --- Main Query for Filtered and Paginated Products ---
  const {
    // data: filteredProductsQueryData, // Full API response
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ApiProductsFullResponse, Error>(
    ['filtered_products', filterPayloadForApi, currentPage, itemsPerPage],
    () => get_filtered_products_new(
      filterPayloadForApi.categoryId,
      filterPayloadForApi.subcategoryId,
      filterPayloadForApi.status,
      currentPage,
      itemsPerPage
      // filterPayloadForApi.searchTerm, // If backend supports search
    ),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const apiPayload = response.data.data; // Access inner 'data' object
        if (apiPayload && apiPayload.products && Array.isArray(apiPayload.products)) {
          setProductList(apiPayload.products);
          if (apiPayload.pagination) {
            setTotalItems(apiPayload.pagination.total);
            setTotalPages(apiPayload.pagination.last_page);
            setCurrentPage(apiPayload.pagination.current_page);
            setCurrentRangeFrom(apiPayload.pagination.from);
            setCurrentRangeTo(apiPayload.pagination.to);
          }
        } else {
          setProductList([]);
          setTotalItems(0);
          setTotalPages(0);
          setCurrentRangeFrom(1);
          setCurrentRangeTo(0);
        }
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to fetch products.", { autoClose: 3000 });
        setProductList([]);
        setTotalItems(0);
        setTotalPages(0);
      },
    }
  );

  // Client-side search (optional)
  const displayedProductList = searchInput
    ? productList.filter((product) =>
        product.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
        product.category_title?.toLowerCase().includes(searchInput.toLowerCase()) ||
        product.sub_category_title?.toLowerCase().includes(searchInput.toLowerCase()) ||
        product.price?.toString().includes(searchInput.toLowerCase())
      )
    : productList;


  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to page 1
    setFilterPayloadForApi({
      categoryId: selectedCategoryId,
      subcategoryId: selectedSubcategoryId,
      status: selectedStatus,
      // searchTerm: searchInput, // If server-side search
    });
  };

  const handleClearFilters = () => {
    setSelectedCategoryId(null);
    setSelectedSubcategoryId(null);
    setSelectedStatus('');
    setSearchInput(''); // Clear client-side search

    setCurrentPage(1);
    setFilterPayloadForApi({
      categoryId: null, subcategoryId: null, status: '', /* searchTerm: '' */
    });
  };

  // Mutations
  const handleDeleteProduct = useMutation(
    (id: number) => delete_product(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Product deleted successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_products', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete product.";
        toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
      },
    }
  );

  const handleStatusUpdate = useMutation(
    (id: number) => change_active_status_product(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Status updated successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_products', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to update status.";
        toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
      },
    }
  );


  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Navigation handlers
  const handleProductLogs = (product: Product) => navigate("/products/product-logs", { state: { id: product.id } });
  const handleEditProduct = (product: Product) => navigate("/products/edit-product", { state: { product: product } });
  const handleViewProduct = (product: Product) => navigate("/products/view-product", { state: { id: product.id } });

  // Removed client-side sorting useEffect and handleSort for now.
  // Server-side sorting would require updating filterPayloadForApi and backend logic.

  if (isLoading && !isFetching && currentPage === 1) {
    return <div className='d-flex justify-content-center align-items-center' style={{height: '200px'}}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
  }

  return (
    <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
      {/* Card Header: Title and Add Button */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Products List</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {totalItems > 0 ? `${totalItems} products found` : 'No products'}
          </span>
        </h3>
        <div className='card-toolbar'>
          {product_add && (
            <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/products/add-product')}>
              <KTIcon iconName='plus' className='fs-3' /> Add Product
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className='card-body pt-0'>
        {/* Row 1: Search, Category, SubCategory */}
        <div className='row mb-4 align-items-center'>
          <div className='col-md-4'> {/* Increased width for search */}
            <div className='input-group'>
              <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='Search name, category, price...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className='col-md-2'>
            <Form.Select
              value={selectedCategoryId || ''}
              className='form-select form-select-solid'
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const catId = e.target.value ? Number(e.target.value) : null;
                setSelectedCategoryId(catId);
                setSelectedSubcategoryId(null); // Reset subcategory when category changes
              }}
            >
              <option value=''>Category...</option>
              {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.title}</option>))}
            </Form.Select>
          </div>
          <div className='col-md-3'> {/* Adjusted width */}
            <Form.Select
              value={selectedSubcategoryId || ''}
              className='form-select form-select-solid'
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedSubcategoryId(e.target.value ? Number(e.target.value) : null)}
              disabled={!selectedCategoryId || filteredSubcategoriesForDropdown.length === 0}
            >
              <option value=''>SubCategory...</option>
              {filteredSubcategoriesForDropdown.map((sub) => (<option key={sub.id} value={sub.id}>{sub.title}</option>))}
            </Form.Select>
          </div>
          <div className='col-md-3'> {/* Adjusted width */}
            <Form.Select
              value={selectedStatus}
              className='form-select form-select-solid'
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value=''>Status...</option>
              <option value='1'>Active</option>
              <option value='0'>Inactive</option>
            </Form.Select>
          </div>
        </div>
        {/* Row 2: Filter Buttons */}
        <div className='row'>
          <div className='col-12 d-flex justify-content-end'>
            <button className='btn btn-light-danger me-3' onClick={handleClearFilters} disabled={isFetching}>
              <KTIcon iconName='cross' className='fs-4 me-1' /> Clear
            </button>
            <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching}>
              <KTIcon iconName='filter-search' className='fs-4 me-1' /> Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body py-3'>
        {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating product list...</i></div>}
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-70px rounded-start'>Picture</th>
                <th className='min-w-150px'>Name</th>
                <th className='min-w-100px'>Price ({getSettingValue('currency_symbol')})</th>
                <th className='min-w-120px'>Category</th>
                <th className='min-w-120px'>SubCategory</th>
                <th className='min-w-100px'>Stock</th>
                <th className='min-w-100px'>Status</th>
                <th className='min-w-150px'>Updated By</th>
                <th className='min-w-150px'>Updated At</th>
                {(product_delete || product_edit || product_logs || product_view) &&
                  <th className='min-w-150px text-center rounded-end'>Actions</th>
                }
              </tr>
            </thead>
            <tbody>
              {isLoading && currentPage === 1 ? (
                <tr><td colSpan={10} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
              ) : isError ? (
                <tr><td colSpan={10} className="text-center py-5 text-danger">Error: {error?.message}</td></tr>
              ) : displayedProductList.length > 0 ? (
                displayedProductList.map((product) => (
                  <tr key={product.id}>
                    <td className='ps-4'>
                      <div className='symbol symbol-50px'>
                        <img
                            src={product.picture || imagePath} // Use fallback imagePath
                            alt={product.name || 'Product'}
                            onError={(e) => { (e.target as HTMLImageElement).src = imagePath; }} // Fallback on error
                        />
                      </div>
                    </td>
                    <td><span className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6 cursor-pointer' onClick={() => handleViewProduct(product)}>{product.name}</span><span className='text-muted fw-semibold text-muted d-block fs-7'>{product.short_description?.substring(0,30) || ''}...</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{product.price}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{product.category_title}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{product.sub_category_title}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{product.remaining_stock_quantity}</span></td>
                    <td>
                      <span className={`badge ${product.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fs-7 fw-bold`}>
                        {product.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{product.updated_by_name || 'N/A'}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{formatDateWithTime(product.updated_at)}</span></td>
                    {(product_delete || product_edit || product_logs || product_view) &&
                    <td className='text-center'>
                      <div className='d-flex justify-content-center flex-shrink-0'>
                        {product_view && (<button onClick={() => handleViewProduct(product)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View"><KTIcon iconName='eye' className='fs-3' /></button>)}
                        {product_edit && (<button onClick={() => handleEditProduct(product)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                        {(product_add || product_edit) && (<button onClick={() => handleStatusUpdate.mutate(product.id)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Toggle Status"><KTIcon iconName='switch' className='fs-3' /></button>)}
                        {product_logs && (<button onClick={() => handleProductLogs(product)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                        {product_delete && (<button onClick={() => setSelectedProduct(product)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_product'><KTIcon iconName='trash' className='fs-3' /></button>)}
                      </div>
                    </td>
                    }
                  </tr>
                ))
              ) : (
                !isFetching && <tr><td colSpan={10} className="text-center py-5">No products found matching your criteria.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: Pagination and Items Per Page */}
      {totalItems > 0 && (
      <div className='card-footer d-flex justify-content-between align-items-center py-4'>
          <div>
            <span className='text-muted me-2'>Showing:</span>
            <select
              className='form-select form-select-sm d-inline-block w-auto me-3'
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              disabled={isFetching}
            >
              {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
            </select>
            <span className='text-muted'>
              {currentRangeFrom} - {currentRangeTo > totalItems ? totalItems : currentRangeTo} of {totalItems} products
            </span>
          </div>
        
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 || isFetching ? 'disabled' : ''}`}>
                <button className="page-link" onClick={handlePreviousPage} disabled={currentPage === 1 || isFetching}>Previous</button>
              </li>
              {/* Optional: Add Page Number Logic here if complex pagination UI is needed */}
              <li className={`page-item ${currentPage === totalPages || totalPages === 0 || isFetching ? 'disabled' : ''}`}>
                <button className="page-link" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0 || isFetching}>Next</button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <div className='modal fade' tabIndex={-1} id='kt_modal_delete_product'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Delete Product Confirmation</h5>
              <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
            </div>
            <div className='modal-body'>
              <p>Are you sure you want to delete the product: <br />
                <strong>{selectedProduct?.name}</strong>?
              </p>
              <p className='text-muted small'>This action cannot be undone.</p>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' data-bs-dismiss='modal'>Cancel</button>
              <button
                type='button'
                className='btn btn-danger'
                onClick={() => { if (selectedProduct) handleDeleteProduct.mutate(selectedProduct.id); }}
                data-bs-dismiss='modal'
                disabled={handleDeleteProduct.isLoading}
              >
                {handleDeleteProduct.isLoading ? 'Deleting...' : 'Delete Product'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default ProductsList;